import React, { useState } from 'react';
import api, { uploadMedia } from '../utils/api';
import './AddFacultyDialog.css';

const AddFacultyDialog = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    coverImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCoverSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await uploadMedia(file);
      if (result.success) {
        setFormData(prev => ({ ...prev, coverImage: result.url }));
      } else {
        setError(result.error || 'Cover image upload failed.');
      }
    } catch (err) {
      setError(err.message || 'Cover image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveCover = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/faculties', formData);
      
      if (response.data.success) {
        onSave(response.data.data);
        onClose();
        // Reset form
        setFormData({ name: '', nameEn: '', coverImage: '' });
      } else {
        setError(response.data.message || 'Failed to create faculty');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Missing required fields. Please fill in both names.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form and error when closing
      setFormData({ name: '', nameEn: '', coverImage: '' });
      setError('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Add New Faculty</h2>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="name">Faculty Name (Bengali) *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter faculty name in Bengali"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="nameEn">Faculty Name (English) *</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleChange}
              required
              placeholder="Enter faculty name in English"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="coverImage">Faculty Card Image</label>
            <div className={`cover-upload ${formData.coverImage ? 'has-image' : ''}`}>
              {formData.coverImage ? (
                <img src={formData.coverImage} alt="Faculty cover" loading="lazy" decoding="async" />
              ) : (
                <div className="cover-placeholder">
                  <span className="cover-icon">+</span>
                  <span>Upload cover image</span>
                </div>
              )}
            </div>
            <div className="cover-actions">
              <label className={`upload-cover-button ${uploading || loading ? 'disabled' : ''}`} htmlFor="coverImage">
                {uploading ? 'Uploading...' : formData.coverImage ? 'Change Image' : 'Upload Image'}
              </label>
              {formData.coverImage && (
                <button type="button" className="remove-cover-button" onClick={handleRemoveCover} disabled={loading || uploading}>
                  Remove
                </button>
              )}
            </div>
            <input
              type="file"
              id="coverImage"
              accept="image/*"
              onChange={handleCoverSelect}
              disabled={loading || uploading}
              className="file-input-hidden"
            />
          </div>
          
          <div className="dialog-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={loading || uploading}
            >
              {loading ? 'Creating...' : 'Create Faculty'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFacultyDialog;
